import { Link, useLocation } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  Bell,
  BarChart3,
  Settings,
  LogOut,
  CheckSquare,
  UserPlus,
  Crown,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useBilling } from "../contexts/BillingContext";
import { useWorkLabel } from "../lib/workLabel";
import { WORKSPACES } from "../lib/workspaces";
import { cn } from "../lib/utils";

const dashboardItems = [
  { to: "/personal", workspace: WORKSPACES.personal, testid: "nav-personal" },
  { to: "/family", workspace: WORKSPACES.family, testid: "nav-family" },
  { to: "/work", workspace: WORKSPACES.work, testid: "nav-work" },
  { to: "/school", workspace: WORKSPACES.school, testid: "nav-school" },
];

const toolItems = [
  { to: "/calendar", icon: CalendarIcon, label: "Calendar", testid: "nav-calendar" },
  { to: "/templates", icon: Sparkles, label: "Templates", testid: "nav-templates" },
  { to: "/notifications", icon: Bell, label: "Notifications", testid: "nav-notifications" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", testid: "nav-analytics" },
  { to: "/family-workspace", icon: UserPlus, label: "Family sharing", testid: "nav-family-workspace" },
  { to: "/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
];

export default function Sidebar({ onClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { status: billing } = useBilling();
  const workLabel = useWorkLabel({ isAuthed: !!user });
  const isPro = !!billing?.is_pro;
  const isTrialing = billing?.subscription_status === "trialing";

  return (
    <aside
      data-testid="app-sidebar"
      className="flex flex-col h-full w-64 bg-[hsl(var(--sidebar-bg))] border-r border-border"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-6 h-16 border-b border-border">
        <div className="relative w-8 h-8 rounded-md bg-primary flex items-center justify-center">
          <CheckSquare size={18} className="text-primary-foreground" strokeWidth={2.2} />
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary pulse-dot" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-extrabold text-base tracking-tight">
            FlowTasks<span className="text-primary">360</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Productivity OS</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {/* Dashboards — each one has its own workspace colour */}
        <div>
          <div className="px-3 mb-2 text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
            Workspaces
          </div>
          <ul className="space-y-0.5">
            {dashboardItems.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.workspace.icon;
              const color = item.workspace.color;
              const displayLabel =
                item.workspace.key === "work" && workLabel
                  ? workLabel
                  : item.workspace.label;
              const style = active
                ? {
                    backgroundColor: `${color}1A`,
                    borderLeft: `2px solid ${color}`,
                    paddingLeft: 10,
                    color: color,
                  }
                : {};
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    data-testid={item.testid}
                    style={style}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                      active
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    )}
                  >
                    <Icon
                      size={16}
                      strokeWidth={1.7}
                      style={active ? { color } : {}}
                      aria-hidden="true"
                    />
                    <span style={active ? { color } : {}}>{displayLabel}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Tools */}
        <div>
          <div className="px-3 mb-2 text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
            Tools
          </div>
          <ul className="space-y-0.5">
            {toolItems.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    data-testid={item.testid}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                      active
                        ? "bg-secondary text-foreground border-l-2 border-primary pl-[10px]"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    )}
                  >
                    <Icon size={16} strokeWidth={1.7} aria-hidden="true" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* User block */}
      <div className="px-3 pb-3 pt-3 border-t border-border">
        {!isPro && billing && typeof billing?.limits?.task_limit === "number" && (
          <UsageMeter
            used={billing?.usage?.tasks ?? 0}
            limit={billing.limits.task_limit}
            onClose={onClose}
          />
        )}
        {!isPro && (
          <Link
            to="/pricing"
            onClick={onClose}
            data-testid="sidebar-upgrade-btn"
            className="flex items-center gap-2 mb-2 px-3 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition"
          >
            <Crown size={14} strokeWidth={2} />
            <span>Upgrade to Pro</span>
          </Link>
        )}
        <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-secondary/50">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm">
            {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5" data-testid="sidebar-user-name">
              {user?.name || "User"}
              {isPro && (
                <span
                  data-testid="sidebar-pro-badge"
                  title={isTrialing ? "Pro trial" : "Pro plan"}
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded px-1.5 py-0.5"
                >
                  <Crown size={9} strokeWidth={2.5} /> {isTrialing ? "Trial" : "Pro"}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout-btn"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
            aria-label="Sign out"
          >
            <LogOut size={15} strokeWidth={1.7} />
          </button>
        </div>
        <p className="px-3 mt-4 text-[10px] leading-relaxed text-muted-foreground">
          © 2026 360DMA • Designed & Engineered by 360 Digital Marketing
        </p>
      </div>
    </aside>
  );
}

/**
 * Tiny "47 / 50 tasks used" pill for Free users. Colour shifts:
 *   <80%  → neutral, calm grey
 *   80%+  → amber warning
 *   95%+  → red, urgent
 * Click anywhere on the pill → /pricing.
 */
function UsageMeter({ used, limit, onClose }) {
  if (!limit || limit <= 0) return null;
  const pct = Math.max(0, Math.min(100, Math.round((used / limit) * 100)));
  const tone =
    pct >= 95 ? "danger" : pct >= 80 ? "warn" : "neutral";
  const palette = {
    neutral: { bar: "#64748B", text: "text-muted-foreground" },
    warn: { bar: "#F59E0B", text: "text-amber-500" },
    danger: { bar: "#EF4444", text: "text-red-500" },
  }[tone];

  return (
    <Link
      to="/pricing"
      onClick={onClose}
      data-testid="sidebar-usage-meter"
      className="block mb-2 px-3 py-2 rounded-md border border-border bg-secondary/40 hover:bg-secondary/70 transition-colors group"
      title={`${used} of ${limit} tasks used — click to upgrade for unlimited`}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1.5">
        <span className="font-bold text-muted-foreground">Tasks</span>
        <span
          className={cn("font-mono font-bold", palette.text)}
          data-testid="sidebar-usage-meter-count"
        >
          {used} / {limit}
        </span>
      </div>
      <div className="h-1 rounded-full bg-border overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: palette.bar }}
        />
      </div>
    </Link>
  );
}
